import { Box } from '@interstate/components/Box';
import { CheckBox } from '@interstate/components/CheckBox';
import { RadioButtonList } from '@interstate/components/RadioButtonList';
import type { FilterOption } from './ProgramsFilters.types';
import { FilterSectionTitle, FilterGroupTitle, FilterOptionList } from './styles';

// Props for FilterSection component
export interface FilterSectionProps {
    filterKey: string;
    label: string;
    options: FilterOption[];
    selectedValues: string[];
    onChange: (value: string) => void;
    useGridLayout?: boolean;
    useRadioButtons?: boolean;
    testIdPrefix?: string;
    readOnly?: boolean;
    useEnhancedTypography?: boolean;
}

const GROUP_GRID_GAP = '1.5rem';
const GROUP_GRID_MAX_COLUMNS = 3;
const GROUP_GRID_MIN_COLUMN_WIDTH = '10rem';
export const GROUP_GRID_COLUMNS = `repeat(auto-fill, minmax(max(${GROUP_GRID_MIN_COLUMN_WIDTH}, calc((100% - ${
    GROUP_GRID_MAX_COLUMNS - 1
} * ${GROUP_GRID_GAP}) / ${GROUP_GRID_MAX_COLUMNS})), 1fr))`;

// Reusable filter section component with checkbox list
export const FilterSection = ({
    filterKey,
    label,
    options,
    selectedValues,
    onChange,
    useGridLayout = false,
    useRadioButtons = false,
    testIdPrefix,
    readOnly = false,
    useEnhancedTypography = false
}: FilterSectionProps) => {
    const testId = testIdPrefix || filterKey;

    const renderTitle = (bold: boolean) => (
        <FilterSectionTitle variant="label-md" data-testid={`${testId}-title`} $bold={bold} $enhanced={useEnhancedTypography}>
            {label}
        </FilterSectionTitle>
    );

    if (readOnly) {
        return <Box data-testid={`${testId}-filter-section`}>{renderTitle(false)}</Box>;
    }

    if (options.length === 0) {
        return null;
    }

    if (useRadioButtons) {
        const radioOptions = options.map((option) => ({
            label: option.label,
            value: option.value
        }));

        return (
            <Box data-testid={`${testId}-filter-section`}>
                {renderTitle(true)}
                <RadioButtonList
                    label=""
                    name={filterKey}
                    data-testid={`${testId}-radio-group`}
                    options={radioOptions}
                    value={selectedValues[0] || ''}
                    onChange={(e) => onChange(e.target.value as string)}
                    sx={{
                        '.MuiFormControlLabel-label': {
                            whiteSpace: 'nowrap'
                        }
                    }}
                />
            </Box>
        );
    }

    const hasGroups = options.some((option) => option.group);
    const groups = hasGroups ? groupOptions(options) : [{ group: undefined, options }];

    return (
        <Box data-testid={`${testId}-filter-section`}>
            {renderTitle(true)}
            <Box
                data-testid={hasGroups ? `${testId}-groups` : undefined}
                sx={hasGroups ? { display: 'grid', gridTemplateColumns: GROUP_GRID_COLUMNS, gap: GROUP_GRID_GAP } : undefined}
            >
                {groups.map(({ group, options: groupOptionsList }) => (
                    <Box key={group ?? '__ungrouped__'} sx={hasGroups ? { minWidth: 0, overflowWrap: 'anywhere' } : undefined}>
                        {group && (
                            <FilterGroupTitle
                                variant="label-sm"
                                data-testid={`${testId}-group-${group}`}
                                $enhanced={useEnhancedTypography}
                            >
                                {group}
                            </FilterGroupTitle>
                        )}
                        <FilterOptionList
                            data-testid={`${testId}-options`}
                            $gridLayout={useGridLayout}
                            $enhanced={useEnhancedTypography}
                        >
                            {groupOptionsList.map((option) => (
                                <CheckBox
                                    key={option.value}
                                    label={option.label}
                                    name={`${filterKey}-${option.value}`}
                                    value={option.value}
                                    data-testid={`${testId}-checkbox-${option.value}`}
                                    checked={selectedValues.includes(option.value)}
                                    onChange={() => onChange(option.value)}
                                />
                            ))}
                        </FilterOptionList>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const groupOptions = (options: FilterOption[]): { group: string | undefined; options: FilterOption[] }[] => {
    const byGroup = new Map<string, FilterOption[]>();

    options.forEach((option) => {
        const key = option.group ?? '';
        const groupedOptions = byGroup.get(key);
        if (groupedOptions) {
            groupedOptions.push(option);
        } else {
            byGroup.set(key, [option]);
        }
    });

    return Array.from(byGroup, ([key, groupedOptions]) => ({ group: key || undefined, options: groupedOptions }));
};
